import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import Button from "@mui/material/Button";
import Tooltip from '@mui/material/Tooltip';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomLoader from "../components/CustomLoader";
import { getAllEmailLogs, getEmailLogDetails } from "../services/adminEmailLogs";
import { CustomAlert, showToastAlert } from "./components/CustomAlert";

const AdminLogMails = () => {
    const [loaderOpen, setLoaderOpen] = React.useState(true);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [apiData, setApiData] = React.useState([]);
    const [currEmailLogId, setCurrEmailLogId] = React.useState("");
    const [emailLogDetails, setEmailLogDetails] = React.useState(null);
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 10,
        page: 0,
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const admin = useSelector((state) => state.admin);

    const tableColumns = [
        {
            field: "to",
            headerName: "Email To",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                <p>{params.row.to}</p>
            ),
        },
        {
            field: "subject",
            headerName: "Subject",
            flex: 1.5,
            editable: false,
            renderCell: (params) => (
                <p>{params.row.subject}</p>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.8,
            editable: false,
            renderCell: (params) => (
                <p style={{ color: params.row.status === "sent" ? "green" : "red" }}>
                    {params.row.status}
                </p>
            ),
        },
        {
            field: "emailType",
            headerName: "Type",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                <p>{params.row.emailType}</p>
            ),
        },
        {
            field: "sentAt",
            headerName: "Sent At",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                <p>
                    {new Date(params.row.sentAt).toLocaleString()}
                </p>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            flex: 0.8,
            minWidth: 100,
            editable: false,
            renderCell: (params) => (
                <div>
                    <Tooltip title="View Details">
                        <Button
                            variant="outlined"
                            onClick={() => handleEmailLogView(params)}
                            className="circular-btn me-2"
                            size="small"
                            startIcon={<RemoveRedEyeRoundedIcon />}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getEmailLogs();
    }, [admin, paginationModel]);

    const getEmailLogs = async () => {
        try {
            setLoaderOpen(true);
            const response = await getAllEmailLogs(
                paginationModel.page + 1, 
                paginationModel.pageSize, 
                null, 
                null, 
                admin.token
            );
            setLoaderOpen(false);

            if (response.status !== 200) {
                showToastAlert("Email logs fetch failed", "error");
                return;
            }
            setApiData(response.data.emailLogs);
            setTotalRecords(response.data.pagination.total);
        }
        catch (err) {
            showToastAlert("Error fetching email logs", "error");
            console.error(err);
        }
    }

    const handleEmailLogView = async (params) => {
        const id = params.row._id;
        setCurrEmailLogId(id);
        
        try {
            const response = await getEmailLogDetails(id, admin.token);
            if (response.status !== 200) {
                showToastAlert("Failed to fetch email log details", "error");
                return;
            }
            setEmailLogDetails(response.data.emailLog);
            setModalOpen(true);
        } catch (err) {
            showToastAlert("Error fetching email log details", "error");
            console.error(err);
        }
    };

    const closeEmailLogView = () => {
        setModalOpen(false);
        setEmailLogDetails(null);
        setCurrEmailLogId("");
    }

    return (
        <div>
            <h1 className="page-title-1 my-3 asdsd">Email Logs - Mails</h1>
            <CustomLoader setLoaderOpen={setLoaderOpen} loaderOpen={loaderOpen} />
            
            <Box
                sx={{
                    width: "100%",
                    height: {
                        xs: 300,
                        sm: 400,
                        md: 500,
                        lg: 600,
                        xl: 800,
                    },
                }}
            >
                <DataGrid
                    rows={apiData}
                    columns={tableColumns}
                    getRowId={(row) => row._id}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    rowCount={totalRecords}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    checkboxSelection
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSelector
                />
            </Box>
            
            <CustomAlert />

            {/* Email Log Details Modal */}
            <Dialog open={modalOpen} onClose={closeEmailLogView} maxWidth="sm" fullWidth>
                <DialogTitle>Email Log Details</DialogTitle>
                <DialogContent>
                    {emailLogDetails && (
                        <div style={{ marginTop: "20px" }}>
                            <div style={{ marginBottom: "15px" }}>
                                <strong>To:</strong>
                                <p style={{ marginTop: "5px", wordBreak: "break-all" }}>{emailLogDetails.to}</p>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                                <strong>Subject:</strong>
                                <p style={{ marginTop: "5px" }}>{emailLogDetails.subject}</p>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                                <strong>Email Type:</strong>
                                <p style={{ marginTop: "5px" }}>{emailLogDetails.emailType}</p>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                                <strong>Status:</strong>
                                <p style={{ 
                                    marginTop: "5px", 
                                    color: emailLogDetails.status === "sent" ? "green" : "red",
                                    fontWeight: "bold"
                                }}>
                                    {emailLogDetails.status}
                                </p>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                                <strong>Sent At:</strong>
                                <p style={{ marginTop: "5px" }}>
                                    {new Date(emailLogDetails.sentAt).toLocaleString()}
                                </p>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                                <strong>Company:</strong>
                                <p style={{ marginTop: "5px" }}>{emailLogDetails.company}</p>
                            </div>
                            {emailLogDetails.body && (
                                <div style={{ marginBottom: "15px" }}>
                                    <strong>Body:</strong>
                                    <div style={{ 
                                        marginTop: "5px", 
                                        padding: "10px",
                                        backgroundColor: "#f5f5f5",
                                        borderRadius: "4px",
                                        maxHeight: "200px",
                                        overflow: "auto",
                                    }}
                                    dangerouslySetInnerHTML={{ __html: emailLogDetails.body }}
                                    />
                                </div>
                            )}
                            {emailLogDetails.error && (
                                <div style={{ marginBottom: "15px" }}>
                                    <strong>Error:</strong>
                                    <p style={{ 
                                        marginTop: "5px",
                                        color: "red",
                                        backgroundColor: "#ffe6e6",
                                        padding: "10px",
                                        borderRadius: "4px"
                                    }}>
                                        {emailLogDetails.error}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEmailLogView} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AdminLogMails;
